import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { gsap } from 'gsap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { Alert } from '@pkg/components';
import { DepButton, DepButtonDisplay } from '@pkg/components/deprecated';
import { useAppDispatch, useAppSelector, useAuthentication } from '@pkg/hooks';
import { AuthRedirectType } from '@pkg/hooks/authentication/types';

import { apiFactory, Cart as CartAPI } from '@app/client';
import { getGlowLevel } from '@app/helpers';
import { setOrderItems, setReduxCartItems } from '@app/store/actions/picker';
import { useInteractionsStore } from '@app/store/zustand/interactions';

import './Cart.scss';
import YatCartItem from './CartItem/CartItem';

const Cart = () => {
    const { toggleCartOpen } = useInteractionsStore();
    const { t } = useTranslation(['yat_picker', 'words', 'payment']);
    const dispatch = useAppDispatch();
    const nodeRef = useRef(null);
    const orderItems = useAppSelector((rootState) => rootState.picker.orderItems);
    const features = useAppSelector((rootState) => rootState.featureFlags.features);
    const isRegisteredUser = useAppSelector((rootState) => rootState.user.isRegisteredUser);

    const { authenticate } = useAuthentication();
    const navigate = useNavigate();

    const [loadingCart, setLoadingCart] = useState(false);
    const [hasError, setError] = useState(false);
    const [removed, setRemoved] = useState('');

    const generation = 1;

    const errorElement = (
        <div className="yat-picker-cart__error-messages">
            <Alert
                alertIcon
                variant="danger"
                message="One or more Yats in your cart have already been claimed. Your total has automatically been updated."
            />
        </div>
    );

    const totalPrice = useMemo(() => {
        return orderItems
            .filter((orderItem) => !orderItem.marked_invalid_at)
            .map((orderItem) => orderItem.unit_price_in_cents / 100)
            .reduce((a, b) => a + b, 0);
    }, [orderItems]);

    const ctaVariant = useMemo(() => {
        const maxScore = Math.max(...orderItems.map((orderItem) => orderItem.rhythm_score));
        return getGlowLevel(maxScore);
    }, [orderItems]);

    const handleRemoveOrder = useCallback(
        (orderItemId: string) => {
            setLoadingCart(true);
            setRemoved(orderItemId);
            apiFactory()
                .call(CartAPI.deleteOrderItem, { id: orderItemId })
                .then((r) => {
                    if (r.ok) {
                        dispatch(setReduxCartItems(r.value));
                    }
                })
                .finally(() => {
                    setLoadingCart(false);
                });
        },
        [dispatch],
    );

    useEffect(() => {
        setError(!!orderItems.find((orderItem) => !!orderItem.marked_invalid_at));
    }, [orderItems]);

    const deleteItemAnimation = useCallback((node) => {
        gsap.timeline()
            .to(node, { autoAlpha: 0, duration: 0.25 })
            .to(node, { height: 0, padding: 0, duration: 0.45, ease: 'power4.out' });
    }, []);

    const handleClearCart = useCallback(() => {
        const confirm = window.confirm(t('clear_cart_confirmation'));
        if (confirm) {
            setLoadingCart(true);
            apiFactory()
                .call(CartAPI.deleteCart, {})
                .then((r) => {
                    if (r.ok) {
                        dispatch(setOrderItems(r.value.order_items));
                        toggleCartOpen(false);
                    }
                })
                .finally(() => {
                    setLoadingCart(false);
                });
        }
    }, [t, dispatch, toggleCartOpen]);

    const handleCheckoutButton = useCallback(() => {
        if (isRegisteredUser) {
            navigate('/checkout');
        } else {
            authenticate({ redirect: { type: AuthRedirectType.Checkout } });
        }
        toggleCartOpen();
    }, [toggleCartOpen, authenticate, isRegisteredUser, navigate]);

    return (
        <div className="yat-picker-cart">
            <div className="yat-picker-cart__header">
                <h2 id="yat-picker-cart-title" className="yat-picker-cart__title">
                    {t('words:cart')}
                </h2>
                {features.clearCartButton && (
                    <button className="yat-picker-cart__clear-cart-button" onClick={handleClearCart}>
                        {t('clear_cart')}
                    </button>
                )}
            </div>
            <div className="yat-picker-cart__scroll-container">
                {hasError && errorElement}
                <TransitionGroup component="ul" enter={false} className="yat-picker-cart__list">
                    {orderItems.map((orderItem) => (
                        <CSSTransition
                            nodeRef={nodeRef}
                            key={orderItem.emoji_id}
                            timeout={750}
                            onExit={deleteItemAnimation}
                            unmountOnExit
                        >
                            <YatCartItem
                                yat={orderItem.emoji_id}
                                rhythmScore={orderItem.rhythm_score}
                                generation={generation}
                                price={orderItem.unit_price_in_cents / 100}
                                orderItemId={orderItem.id}
                                handleRemoveOrder={handleRemoveOrder}
                                taken={!!orderItem.marked_invalid_at}
                                removedId={removed}
                            />
                        </CSSTransition>
                    ))}
                </TransitionGroup>
            </div>
            <div className="yat-picker-cart__bottom">
                <div className="yat-picker-cart__summary">
                    <span className="v2-text-base">{t('payment:total')}</span>
                    <span className="v2-text-lg">${totalPrice}</span>
                </div>
                <DepButton
                    // state={{ walletLinkCreation: locationState?.walletLinkCreation }}
                    onClick={handleCheckoutButton}
                    variant={ctaVariant}
                    display={DepButtonDisplay.block}
                    glow
                    loading={loadingCart}
                    disabled={orderItems.length === 0}
                >
                    {t('continue_to_checkout')}
                </DepButton>
                <span className="yat-picker-cart__bottom-text v2-text-xs">{t('pay_once_own_forever')}</span>
            </div>
        </div>
    );
};

export default Cart;
